import { ApiQuery, SortDirections, SortQuery } from "~/modules/filters/types";
import { useState } from "react";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";

// // // //

/**
 * ASSET_SORT_MAP
 * Map each AssetColumns to a key used to in-memory sort the array of items using `sortItemsByNestedProperty`
 */
export const ASSET_SORT_MAP: Record<AssetColumns, string> = {
    [AssetColumns.name]: "name",
    // [AssetColumns.marketValueDollar]: "market_value_dollar.val",
    [AssetColumns.ticker]: "ticker",
    [AssetColumns.legalStructureCountryName]: "issuer.legal_structure_country",
    [AssetColumns.regulatoryFramework]: "regulatory_framework",
    [AssetColumns.underlyingAssetClasses]: "underlying_asset_classes",
    [AssetColumns.networkIDs]: "network_ids",
    // [AssetColumns.priceDollar]: "price_dollar.val",
    [AssetColumns.peggedAssetName]: "pegged_asset.name",
    // [AssetColumns.weeklyActiveAddressesCount]:
    //     "weekly_active_addresses_count.val",
    [AssetColumns.governingBody]: "governing_body",
    ["are_holdings_bankruptcy_remote" as AssetColumns]:
        "are_holdings_bankruptcy_remote",
    ["are_accounts_segregated" as AssetColumns]: "are_accounts_segregated",
    [AssetColumns.custodyDescription]: "custody_description",
    [AssetColumns.transparencyType]: "transparency_type",
    [AssetColumns.primaryMarketSubscriptionFeeBps]:
        "primary_market.subscription_fee_bps.val",
    [AssetColumns.primaryMarketRedemptionFeeBps]:
        "primary_market.redemption_fee_bps.val",
    ["auditor.name" as AssetColumns]: "auditor.name",
    ["traditional_custodian" as AssetColumns]: "traditional_custodian.name",
    [AssetColumns.issuerName]: "issuer.name",
    ["spRating" as AssetColumns]: "sp_rating",
    // // // //
    // New Measures
    [AssetColumns.yieldToMaturityPercent]: "yield_to_maturity_percent.val",
    [AssetColumns.apy7Day]: "apy_7_day.val",
    [AssetColumns.apy30Day]: "apy_30_day.val",
    [AssetColumns.cashDragPercent]: "cash_drag_percent.val",
    [AssetColumns.defaultedLoansAmountPercent]:
        "defaulted_loans_amount_percent.val",
    [AssetColumns.netAssetValueDollar]: "net_asset_value_dollar.val",
    [AssetColumns.priceDollar]: "price_dollar.val",
    [AssetColumns.m2MoneyStockDollar]: "m2_money_stock_dollar.val",
    [AssetColumns.dailyTransferVolumeDollar]:
        "daily_transfer_volume_dollar.val",
    [AssetColumns.cumulativeRepaidPrincipalDollar]:
        "cumulative_repaid_principal_dollar.val",
    [AssetColumns.fundedAssetsDollar]: "funded_assets_dollar.val",
    [AssetColumns.availableCapacityDollar]: "available_capacity_dollar.val",
    [AssetColumns.interestPaidDollar]: "interest_paid_dollar.val",
    [AssetColumns.idleCapitalDollar]: "idle_capital_dollar.val",
    [AssetColumns.outstandingPrincipalDollar]:
        "outstanding_principal_dollar.val",
    [AssetColumns.cumulativeIssuedAmountDollar]:
        "cumulative_issued_amount_dollar.val",
    [AssetColumns.writtenOffPrincipalDollar]:
        "written_off_principal_dollar.val",
    [AssetColumns.outstandingCapitalDollar]: "outstanding_capital_dollar.val",
    [AssetColumns.principalPaidDollar]: "principal_paid_dollar.val",
    [AssetColumns.circulatingAssetValueDollar]:
        "circulating_asset_value_dollar.val",
    [AssetColumns.totalAssetValueDollar]: "total_asset_value_dollar.val",
    [AssetColumns.drawndownAssetsDollar]: "drawndown_assets_dollar.val",
    [AssetColumns.cumulativeInterestEarnedDollar]:
        "cumulative_interest_earned_dollar.val",
    [AssetColumns.dailyMintsDollar]: "daily_mints_dollar.val",
    [AssetColumns.dailyBurnsDollar]: "daily_burns_dollar.val",
    [AssetColumns.adjustedDailyMintsDollar]: "adjusted_daily_mints_dollar.val",
    [AssetColumns.adjustedDailyBurnsDollar]: "adjusted_daily_burns_dollar.val",
    [AssetColumns.recoveredAssetsDollar]: "recovered_assets_dollar.val",
    [AssetColumns.maximumCapacityDollar]: "maximum_capacity_dollar.val",
    [AssetColumns.marketValueDollar]: "market_value_dollar.val",
    [AssetColumns.bridgedTokenMarketCapDollar]:
        "bridged_token_market_cap_dollar.val",
    [AssetColumns.bridgedTokenSupplyToken]: "bridged_token_supply_token.val",
    [AssetColumns.bridgedTokenValueDollar]: "bridged_token_value_dollar.val",
    [AssetColumns.circulatingMarketValueDollar]:
        "circulating_market_value_dollar.val",
    [AssetColumns.cumulativeInterestEarnedToken]:
        "cumulative_interest_earned_token.val",
    [AssetColumns.cumulativeIssuedAmountToken]:
        "cumulative_issued_amount_token.val",
    [AssetColumns.availableCapacityToken]: "available_capacity_token.val",
    [AssetColumns.dailyBurnsToken]: "daily_burns_token.val",
    [AssetColumns.adjustedDailyBurnsToken]: "adjusted_daily_burns_token.val",
    [AssetColumns.drawndownAssetsToken]: "drawndown_assets_token.val",
    [AssetColumns.writtenOffPrincipalToken]: "written_off_principal_token.val",
    [AssetColumns.totalSupplyToken]: "total_supply_token.val",
    [AssetColumns.defaultedLoansAmountToken]:
        "defaulted_loans_amount_token.val",
    [AssetColumns.defaultedLoansAmountDollar]:
        "defaulted_loans_amount_dollar.val",
    [AssetColumns.recoveredAssetsToken]: "recovered_assets_token.val",
    [AssetColumns.principalPaidToken]: "principal_paid_token.val",
    [AssetColumns.dailyTransferVolumeToken]: "daily_transfer_volume_token.val",
    [AssetColumns.dailyMintsToken]: "daily_mints_token.val",
    [AssetColumns.adjustedDailyMintsToken]: "adjusted_daily_mints_token.val",
    [AssetColumns.cumulativeRepaidPrincipalToken]:
        "cumulative_repaid_principal_token.val",
    [AssetColumns.outstandingPrincipalToken]: "outstanding_principal_token.val",
    [AssetColumns.circulatingSupplyToken]: "circulating_supply_token.val",
    [AssetColumns.interestPaidToken]: "interest_paid_token.val",
    [AssetColumns.idleCapitalToken]: "idle_capital_token.val",
    [AssetColumns.adjustedHoldingAddressesCount]:
        "adjusted_holding_addresses_count.val",
    [AssetColumns.trailing7DayActiveAddressesCount]:
        "trailing_7_day_active_addresses_count.val",
    [AssetColumns.annuallyActiveAddressesCount]:
        "annually_active_addresses_count.val",
    [AssetColumns.trailing30DayActiveAddressesCount]:
        "trailing_30_day_active_addresses_count.val",
    [AssetColumns.dailyActiveAddressesCount]:
        "daily_active_addresses_count.val",
    [AssetColumns.trailing365DayActiveAddressesCount]:
        "trailing_365_day_active_addresses_count.val",
    [AssetColumns.weeklyActiveAddressesCount]:
        "weekly_active_addresses_count.val",
    [AssetColumns.trailing90DayActiveAddressesCount]:
        "trailing_90_day_active_addresses_count.val",
    [AssetColumns.quarterlyActiveAddressesCount]:
        "quarterly_active_addresses_count.val",
    [AssetColumns.holdingAddressesCount]: "holding_addresses_count.val",
    [AssetColumns.numberOfDailyTransactionsCount]:
        "number_of_daily_transactions_count.val",
    [AssetColumns.monthlyActiveAddressesCount]:
        "monthly_active_addresses_count.val",
    [AssetColumns.weeklyTransferCount]: "weekly_transfer_count.val",
    [AssetColumns.monthlyTransferCount]: "monthly_transfer_count.val",
    [AssetColumns.trailing7DayTransferCount]:
        "trailing_7_day_transfer_count.val",
    [AssetColumns.trailing30DayTransferCount]:
        "trailing_30_day_transfer_count.val",
    [AssetColumns.weeklyTransferVolume]: "weekly_transfer_volume.val",
    [AssetColumns.monthlyTransferVolume]: "monthly_transfer_volume.val",
    [AssetColumns.trailing7DayTransferVolume]:
        "trailing_7_day_transfer_volume.val",
    [AssetColumns.trailing30DayTransferVolume]:
        "trailing_30_day_transfer_volume.val",
    [AssetColumns.weightedAverageMaturityYears]:
        "weighted_average_maturity_years.val",
    [AssetColumns.numberOfDefaultedLoans]: "number_of_defaulted_loans.val",
    [AssetColumns.numberOfActiveLoans]: "number_of_active_loans.val",
    [AssetColumns.numberOfLoansFunded]: "number_of_loans_funded.val",
    [AssetColumns.assetClassName]: "asset_class_name",
    [AssetColumns.hasSingleHolding]: "has_single_holding",
    [AssetColumns.investmentStrategyType]: "investment_strategy_type",
    // // // //
    // Real Estate
    [AssetColumns.realEstateZipCode]: "real_estate_property.zip_code",
    [AssetColumns.realEstateStreetAddress]:
        "real_estate_property.street_address",
    [AssetColumns.realEstateCity]: "real_estate_property.city",
    [AssetColumns.realEstateState]: "real_estate_property.state",
    [AssetColumns.realEstateCountryName]: "real_estate_property.country.name",
    [AssetColumns.realEstateZoningType]:
        "real_estate_property.zoning_type.name",
    [AssetColumns.realEstatePropertyType]:
        "real_estate_property.property_type.name",
    [AssetColumns.tokenizationTypeIDs]: "tokenization_type_ids",
    // // // //
    // All others
    [AssetColumns.id]: "id",
    [AssetColumns.description]: "description",
    [AssetColumns.iconUrl]: "icon_url",
    [AssetColumns.colorHex]: "color_hex",
    [AssetColumns.unit]: "unit",
    [AssetColumns.isin]: "isin",
    [AssetColumns.cusip]: "cusip",
    [AssetColumns.website]: "website",
    [AssetColumns.inceptionDate]: "inception_date",
    [AssetColumns.transparencyDescription]: "transparency_description",
    [AssetColumns.transparencyUrl]: "transparency_url",
    [AssetColumns.holderRightsDescription]: "holder_rights_description",
    [AssetColumns.holderHasRightToRedeem]: "holder_has_right_to_redeem",
    [AssetColumns.hasForkRights]: "has_fork_rights",
    [AssetColumns.forkRightsDescription]: "fork_rights_description",
    [AssetColumns.disputeResolutionCountry]: "dispute_resolution_country",
    [AssetColumns.disputeResolutionDescription]:
        "dispute_resolution_description",
    [AssetColumns.assetToPeggedAssetRatio]: "asset_to_pegged_asset_ratio",
    [AssetColumns.pegMaintenanceDescription]: "peg_maintenance_description",
    [AssetColumns.annualReturnTargetDescription]:
        "annual_return_target_description",
    [AssetColumns.totalManagementFeeBps]: "total_management_fee_bps",
    [AssetColumns.totalPerformanceFeeBps]: "total_performance_fee_bps",
    [AssetColumns.feeStructureDescription]: "fee_structure_description",
    [AssetColumns.otherFeesDescription]: "other_fees_description",
    [AssetColumns.legalStructureDescription]: "legal_structure_description",
    [AssetColumns.legalStructureCountryID]: "legal_structure_country_id",
    [AssetColumns.legalStructureCountryIDRaw]: "legal_structure__country_id",
    [AssetColumns.jurisdictionCountryID]: "jurisdiction_country_id",
    [AssetColumns.jurisdictionCountryName]: "jurisdiction_country_name",
    [AssetColumns.managementMechanismType]: "management_mechanism_type",
    [AssetColumns.poolHoldingsAreAllSecured]: "pool_holdings_are_all_secured",
    [AssetColumns.poolHoldingsSecurityDescription]:
        "pool_holdings_security_description",
    [AssetColumns.type]: "type",
    [AssetColumns.slug]: "slug",
    [AssetColumns._created_at]: "_created_at",
    [AssetColumns.protocolIDs]: "protocol_ids",
    [AssetColumns.platformIDs]: "platform_ids",
    [AssetColumns.protocolNames]: "protocol_names",
    [AssetColumns.baseProtocolIDs]: "base_protocol_ids",
    [AssetColumns.networkNames]: "network_names",
    [AssetColumns.sectorIDs]: "sector_ids",
    [AssetColumns.regionIDs]: "region_ids",
    [AssetColumns.tokenCount]: "token_count",
    [AssetColumns.tokenizationSponsorshipType]: "tokenization_sponsorship_type",
    [AssetColumns.transferabilityTypeIDs]: "transferability_type_ids",
    [AssetColumns.investmentStrategyTypeID]: "investment_strategy_type_id",
    [AssetColumns.denominationAssetID]: "denomination_asset_id",
    [AssetColumns.denominationAssetTicker]: "denomination_asset_ticker",
    [AssetColumns.distributesIncome]: "distributes_income",
    [AssetColumns.distributionFrequencyType]: "distribution_frequency_type",
    [AssetColumns.navReflectsIncomeOnly]: "nav_reflects_income_only",
    [AssetColumns.accountingBasis]: "accounting_basis",
    [AssetColumns.accountingBasisDescription]: "accounting_basis_description",
    [AssetColumns.accountingFrequencyTypeID]: "accounting_frequency_type_id",
    [AssetColumns.lockupPeriodMonths]: "lockup_period_months",
    [AssetColumns.lockupDescription]: "lockup_description",
    [AssetColumns.termLengthYears]: "term_length_years",
    [AssetColumns.termDescription]: "term_description",
    [AssetColumns.isOpenEnded]: "is_open_ended",
    [AssetColumns.issuerID]: "issuer_id",
    [AssetColumns.assetClassID]: "asset_class_id",
    [AssetColumns.peggedAssetID]: "pegged_asset_id",
    [AssetColumns.peggedAssetType]: "pegged_asset_type",
    [AssetColumns.managerID]: "manager_id",
    [AssetColumns.subAdvisorID]: "sub_advisor_id",
    [AssetColumns.traditionalBrokerID]: "traditional_broker_id",
    [AssetColumns.traditionalCustodianID]: "traditional_custodian_id",
    [AssetColumns.cryptoCustodianID]: "crypto_custodian_id",
    [AssetColumns.cryptoBrokerID]: "crypto_broker_id",
    [AssetColumns.auditorID]: "auditor_id",
    [AssetColumns.fundAdministratorID]: "fund_administrator_id",
    [AssetColumns.exchangeAgentID]: "exchange_agent_id",
    [AssetColumns.transferAgentID]: "transfer_agent_id",
    [AssetColumns.payingAgentID]: "paying_agent_id",
    [AssetColumns.primaryMarketID]: "primary_market_id",
    [AssetColumns.primaryMarketSubscriptionFeeBpsV1]:
        "primary_market_subscription_fee_bps_v1",
    [AssetColumns.primaryMarketRedemptionFeeBpsV1]:
        "primary_market_redemption_fee_bps_v1",
    [AssetColumns.primaryMarketIsKYCRequired]: "primary_market_is_kyc_required",
    [AssetColumns.primaryMarketBaseAssetTicker]:
        "primary_market_base_asset_ticker",
    [AssetColumns.primaryMarketMinimumSubscriptionAmount]:
        "primary_market_minimum_subscription_amount",
    [AssetColumns.primaryMarketSubscriptionDescription]:
        "primary_market_subscription_description",
    [AssetColumns.primaryMarketRedemptionDescription]:
        "primary_market_redemption_description",
    [AssetColumns.primaryMarketInvestorTypes]: "primary_market_investor_types",
    [AssetColumns.primaryMarketSubscriptionFrequency]:
        "primary_market_subscription_frequency",
    [AssetColumns.primaryMarketRedemptionFrequency]:
        "primary_market_redemption_frequency",
    [AssetColumns.primaryMarketKYCProviderID]: "primary_market_kyc_provider_id",
    [AssetColumns.primaryMarketNonPeggedAsset]:
        "primary_market_non_pegged_asset",
    [AssetColumns.primaryMarketPeggedAsset]: "primary_market_pegged_asset",
    [AssetColumns.topHolderConcentration]: "top_holder_concentration",
    [AssetColumns.top5HolderConcentration]: "top_5_holder_concentration",
    [AssetColumns.top10HolderConcentration]: "top_10_holder_concentration",
    [AssetColumns.realEstateCountryID]: "real_estate_country_id",
    [AssetColumns.dailyYieldDistributedDollar]:
        "daily_yield_distributed_dollar",
    [AssetColumns.dailyYieldRate]: "daily_yield_rate",
    [AssetColumns.hypothetical_10_000_performance]:
        "hypothetical_10_000_performance",
    [AssetColumns.statsAvailable]: "stats_available",
    [AssetColumns.isInvestable]: "is_investable",
    [AssetColumns.trailing7DayReturnPercent]: "trailing_7_day_return_percent",
    [AssetColumns.trailing1MonthReturnPercent]:
        "trailing_1_month_return_percent",
    [AssetColumns.trailing3MonthReturnPercent]:
        "trailing_3_month_return_percent",
    [AssetColumns.trailing1YearReturnPercent]: "trailing_1_year_return_percent",
    [AssetColumns.yearToDateReturnPercent]: "year_to_date_return_percent",
    [AssetColumns.sinceInceptionReturnPercent]:
        "since_inception_return_percent",
};

// // // //

// ENHANCEMENT - update this to accept an optional ApiQuery object for the default query value
// TODO - this is only used on the TokenTable on the asset individual page, so we can colocate it there once we consolidate those pages
export function useInMemoryAssetSort(props: {
    rows: Asset[];
    sort?: SortQuery;
}) {
    const {
        rows,
        sort = {
            field: AssetColumns.marketValueDollar,
            direction: SortDirections.desc,
        },
    } = props;

    const [query, setQuery] = useState<ApiQuery>({
        sort,
        pagination: {
            page: 1,
            perPage: 25,
        },
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: ASSET_SORT_MAP[query.sort.field as AssetColumns],
        direction: query.sort.direction,
        nullsLast: true,
    }).map((asset) => {
        // Sort Asset.tokens
        const sortedTokens = sortItemsByProperty({
            items: asset.tokens,
            field: ASSET_SORT_MAP[query.sort.field as AssetColumns],
            direction: query.sort.direction,
            nullsLast: true,
        });

        return {
            ...asset,
            tokens: sortedTokens,
        };
    });

    return { query, setQuery, sortedItems };
}
